<template>
  <div class="bp-steps" :class="{ vertical }" :style="style">
    <div
      v-for="(step, i) in steps"
      :key="i"
      class="wrapper"
      :class="getClasses(i)"
    >
      <div class="step">
        <div class="step-button" :class="{ navigable }" @click="click(i)">
          <div class="title">{{ step }}</div>

          <div class="current-step">
            {{ i + 1 }}
          </div>
        </div>

        <div v-if="showContent(i)" class="step-content">
          <slot :name="`step-content-${i + 1}`" />
        </div>
      </div>

      <div class="divider">
        <i v-if="showNextArrow(i)" class="fa-light fa-angle-right text-dark" />

        <div v-if="!isLastStep(i)" class="line" />

        <i
          v-if="showPreviousArrow(i)"
          class="fa-light fa-angle-left text-dark"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: Number,
      default: 1
    },
    steps: {
      type: Array,
      required: true
    },
    navigable: {
      type: Boolean,
      default: false
    },
    vertical: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    click(i) {
      if (this.navigable) this.$emit("input", i + 1);
    },
    isLastStep(i) {
      return i === this.steps.length - 1;
    },
    getClasses(i) {
      return {
        done: this.value > i + 1,
        focused: this.value === i + 1,

        first: i === 0,
        last: this.isLastStep(i)
      };
    },
    showContent(i) {
      return !this.vertical && this.value === i + 1;
    },
    showNextArrow(i) {
      return (
        this.navigable && this.steps.length > i + 1 && this.value === i + 1
      );
    },
    showPreviousArrow(i) {
      return (
        this.navigable && this.steps.length >= i + 2 && this.value === i + 2
      );
    }
  },
  computed: {
    style() {
      const fractions = "1fr ".repeat(this.steps.length - 1);
      return `--fractions: ${fractions}; --steps: ${this.steps.length};`;
    }
  }
};
</script>

<style lang="scss" scoped>
.bp-steps {
  // Horizontal (base) version
  display: grid;
  grid-template-columns: var(--fractions) auto;

  .wrapper {
    display: grid;
    grid-template-rows: none;
    grid-template-columns: auto 1fr;

    .step {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-direction: column;

      max-width: 34px;
      overflow-y: visible;

      .step-button {
        display: flex;
        align-items: center;
        flex-direction: column;

        .title {
          color: var(--dark-40);
          white-space: nowrap;
          transition: color 0.2s;
        }

        .current-step {
          display: flex;
          justify-content: center;

          min-width: 26px;
          margin: 12px 4px;

          border: 1px solid;
          border-radius: 50%;
          border-color: var(--dark-10);
          transition: all 0.2s;
          color: var(--dark-40);
          background-color: white;
        }

        &.navigable {
          cursor: pointer;
          &:hover {
            .title {
              color: var(--dark);
            }
            .current-step {
              box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.2);
            }
          }

          &:active {
            .title {
              color: var(--dark-40);
            }
            .current-step {
              box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.12);
            }
          }
        }
      }
    }

    .divider {
      display: flex;
      height: 100px;
      align-items: center;

      .line {
        width: 100%;
        border: 1px solid;
        color: var(--dark-20);
      }

      i {
        margin: 0 4px;
      }
    }

    // Changes according to the step state
    &.focused {
      .step-button {
        .current-step {
          color: white;
          border-color: var(--dark);
          background-color: var(--dark);
        }
        i,
        .title {
          color: var(--dark);
        }
        &.navigable {
          cursor: default;
        }
      }
    }

    &.done {
      .step-button {
        .current-step {
          color: white;
          border-color: var(--dark);
          background-color: var(--dark);
        }
      }

      .line {
        color: var(--dark);
      }
    }

    // Changes according to the step position
    &.last {
      width: fit-content;
      .step,
      .step-button {
        align-items: end;
      }
    }

    &.first {
      .step,
      .step-button {
        align-items: flex-start;
      }
    }

    // Prevent overflow in small screens
    @media screen and (max-width: 667px) {
      .step-button {
        max-width: 80px;
      }
    }

    @media screen and (max-width: 768px) {
      &:not(.focused) {
        .title {
          visibility: hidden;
        }
      }
    }
  }

  // Changes in layout when vertical
  @media screen and (min-width: 992px) {
    &.vertical {
      height: calc(60px * var(--steps));
      grid-template-columns: none;
      grid-template-rows: var(--fractions) auto;

      .wrapper {
        height: 100%;
        width: fit-content;
        grid-template-columns: none;
        grid-template-rows: auto 1fr;

        .step {
          max-width: unset;
          align-items: flex-start;
          height: fit-content;

          .step-button {
            align-items: center;
            flex-direction: row;

            .title {
              order: 2;
              visibility: visible;
            }

            .current-step {
              margin: 4px 12px 4px 4px;
            }
          }
        }

        .divider {
          width: 34px;
          height: unset;
          flex-direction: column;

          .line {
            width: 1px;
            height: 100%;
          }

          i {
            margin: 4px 0;
            rotate: 90deg;
          }
        }
      }
    }
  }
}
</style>
